import React, { useEffect, useState } from 'react';
import { Field } from '../components/ui/Field';
import Popover from '../components/ui/Popover';
import type { MemberRole, PlanMember } from './types';

const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

const ROLE_RANK: Record<MemberRole, number> = { owner: 0, admin: 1, member: 2 };
const ROLE_LABEL: Record<MemberRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };

let listSeq = 0;

const fullName = (m: Pick<PlanMember, 'firstname' | 'lastname'>) => `${m.firstname || ''} ${m.lastname || ''}`.trim() || 'Unknown';

/** Owner first, then admins, then members, each by first name. */
export const orderMembers = (members: PlanMember[]): PlanMember[] =>
  [...members].sort(
    (a, b) =>
      ROLE_RANK[a.role] - ROLE_RANK[b.role] ||
      (a.firstname || '').localeCompare(b.firstname || '', undefined, { sensitivity: 'base' }) ||
      (a.lastname || '').localeCompare(b.lastname || '', undefined, { sensitivity: 'base' }),
  );

const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0][0] ?? '';
  const last = words.length > 1 ? words[words.length - 1][0] ?? '' : '';
  return `${first}${last}`.toUpperCase();
};

/**
 * Who holds a topic: initials in a 20px face plus the first name. Quiet on purpose, it
 * sits in a heading row. The full name is in the tooltip and read out to a screen reader.
 */
export const AuthorityChip: React.FC<{ name: string | null; className?: string }> = ({ name, className = '' }) => {
  if (!name) return null;
  const first = name.trim().split(/\s+/)[0] || name;
  return (
    <span
      title={`Node authority: ${name}`}
      className={`inline-flex h-5 max-w-[10rem] flex-shrink-0 items-center gap-1.5 rounded-full bg-surface-container-highest pr-2 text-[11px] font-medium leading-none text-on-surface-variant ${className}`}
    >
      <span className="sr-only">Node authority: {name}</span>
      <span
        aria-hidden="true"
        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-surface-container-high text-[9.5px] font-semibold tracking-tight text-on-surface"
      >
        {initialsOf(name)}
      </span>
      <span aria-hidden="true" className="min-w-0 truncate">
        {first}
      </span>
    </span>
  );
};

interface AuthorityListProps {
  /** For the field's accessible name. */
  nodeTitle: string;
  members: PlanMember[];
  currentId: string | null;
  onPick: (userId: string | null) => void;
  onCancel: () => void;
}

interface Choice {
  id: string | null;
  name: string;
  role?: MemberRole;
}

/** Type to narrow, arrows to move, Enter to set. Built like the canvas menu's "Connect to" picker. */
export const AuthorityList: React.FC<AuthorityListProps> = ({ nodeTitle, members, currentId, onPick, onCancel }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [listId] = useState(() => `np-authority-${(listSeq += 1)}`);
  const q = query.trim().toLowerCase();
  const people: Choice[] = orderMembers(members).map((m) => ({ id: m.user_id, name: fullName(m), role: m.role }));
  const all: Choice[] = currentId ? [{ id: null, name: 'No authority' }, ...people] : people;
  const shown = q ? all.filter((c) => c.id !== null && c.name.toLowerCase().includes(q)) : all;
  const at = Math.min(active, Math.max(0, shown.length - 1));
  const optionId = (c: Choice) => `${listId}-${c.id ?? 'none'}`;

  useEffect(() => {
    const el = shown[at] ? document.getElementById(optionId(shown[at])) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at, query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (shown.length === 0) return;
      setActive((at + (e.key === 'ArrowDown' ? 1 : shown.length - 1)) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (shown[at]) onPick(shown[at].id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <div className="p-2">
      <Field
        autoFocus
        value={query}
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={shown[at] ? optionId(shown[at]) : undefined}
        aria-label={`Authority for ${nodeTitle}`}
        placeholder="Find a person"
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {shown.length === 0 ? (
        <p className="px-2 pb-1 pt-3 text-[13px] text-on-surface-variant">No one on this plan matches.</p>
      ) : (
        <ul id={listId} role="listbox" aria-label="Plan members" className="mt-2 max-h-60 space-y-0.5 overflow-y-auto">
          {shown.map((c, i) => {
            const current = c.id !== null && c.id === currentId;
            return (
              <li
                key={c.id ?? 'none'}
                id={optionId(c)}
                role="option"
                aria-selected={i === at}
                title={c.name}
                // Focus stays in the field, so the arrow keys and typing keep working after a click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(c.id)}
                className={`np-state flex min-h-[32px] cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] ${
                  i === at ? 'bg-surface-container-highest text-on-surface' : 'text-on-surface-variant'
                } ${MOTION}`}
              >
                <span className={`min-w-0 flex-1 truncate ${c.id === null ? 'italic' : ''}`}>{c.name}</span>
                {current && <span className="flex-shrink-0 text-[11px] font-semibold text-on-surface">Current</span>}
                {c.role && <span className="flex-shrink-0 text-[11px] text-on-surface-variant">{ROLE_LABEL[c.role]}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

interface AuthorityPickerProps extends Omit<AuthorityListProps, 'onCancel'> {
  /** The control that opened it; the picker hangs under it. */
  anchor: React.RefObject<HTMLElement>;
  onClose: () => void;
}

/**
 * The list in the kit Popover, beside the control that opened it. Closes on a pick, Escape,
 * an outside press or focus moving away.
 */
export const AuthorityPicker: React.FC<AuthorityPickerProps> = ({ anchor, onClose, ...list }) => (
  // The canvas reads keys through React and this sits inside its tree: typing here must not
  // reach its Delete, Escape or arrow keys (the portal does not move the React parent).
  <div className="contents" onKeyDown={(e) => e.stopPropagation()}>
    <Popover open onClose={onClose} anchor={anchor} label={list.currentId ? 'Change authority' : 'Set authority'} width="md">
      <div className="-mx-2 -mb-2 -mt-1">
        <AuthorityList {...list} onCancel={onClose} />
      </div>
    </Popover>
  </div>
);
